/**
 * URL slug generation for businesses.
 *
 * Framework-free and I/O-free on purpose: imported by the api (to mint slugs
 * server-side) AND by the web client (to render a live preview as the owner
 * types), so it must not reach for Prisma, Nest, or the DOM.
 *
 * ⚠️ Hebrew transliteration is LOSSY and cannot be otherwise. Written Hebrew
 * omits vowels, so `מספרת רנה` mechanically becomes `msprt-rnh`, not the
 * `mispara-rena` a human would write. That is why every caller treats the
 * output as a SUGGESTION the owner can edit, never a final answer. A future
 * dictionary of common trade words (מספרה, סלון, ספא…) is a drop-in upgrade
 * to `generateSlug` alone and changes nothing else in the system.
 */

/** Max slug length, including any collision suffix. */
export const SLUG_MAX_LENGTH = 48;

/** Number of characters in a collision suffix. */
export const SLUG_SUFFIX_LENGTH = 4;

/**
 * Suffix alphabet, deliberately excluding `i`, `l`, `o`, `0` and `1` — a slug
 * ends up on a business card and gets retyped by a customer, and those five
 * are the characters people transcribe wrong.
 */
export const SLUG_SUFFIX_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';

/** Used when a name contains nothing slug-able (punctuation/emoji only). */
export const FALLBACK_SLUG = 'business';

/**
 * Hebrew letter -> Latin. Final forms (ך ם ן ף ץ) map to the same sound as
 * their base letter, except ף which is always `f` word-finally.
 *
 * א and ע map to the empty string: they are silent letter-carriers in Hebrew
 * and emitting a vowel for them guesses wrong more often than it guesses right.
 */
const HEBREW_TRANSLITERATION: Readonly<Record<string, string>> = {
  א: '',
  ב: 'b',
  ג: 'g',
  ד: 'd',
  ה: 'h',
  ו: 'o',
  ז: 'z',
  ח: 'ch',
  ט: 't',
  י: 'i',
  כ: 'k',
  ך: 'k',
  ל: 'l',
  מ: 'm',
  ם: 'm',
  נ: 'n',
  ן: 'n',
  ס: 's',
  ע: '',
  פ: 'p',
  ף: 'f',
  צ: 'tz',
  ץ: 'tz',
  ק: 'k',
  ר: 'r',
  ש: 'sh',
  ת: 't',
};

/**
 * Common beauty/wellness trade words, matched whole-word BEFORE the
 * letter-by-letter fallback below. Letter transliteration alone drops every
 * vowel (`מספרה` -> `msprh`), which reads as noise for exactly the words that
 * show up in the most business names. This is the "future dictionary" this
 * file used to only promise in a comment — still partial by design (a name
 * like `רנה` still falls back to letters), so the result stays a SUGGESTION
 * the owner can edit, never a final answer. Extend by adding a row; matching
 * is exact-word only (no prefix stripping for ה/ו/ש/ב/ל/מ), so `הסלון` still
 * falls through to letters.
 */
const HEBREW_TRADE_WORDS: Readonly<Record<string, string>> = {
  מספרה: 'salon',
  מספרת: 'salon',
  סלון: 'salon',
  סטודיו: 'studio',
  ספא: 'spa',
  קליניקה: 'clinic',
  מכון: 'institute',
  יופי: 'beauty',
  קוסמטיקה: 'cosmetics',
  איפור: 'makeup',
  עיסוי: 'massage',
  פדיקור: 'pedicure',
  מניקור: 'manicure',
  ציפורניים: 'nails',
  גבות: 'brows',
  ריסים: 'lashes',
  שיזוף: 'tanning',
  לייזר: 'laser',
  טיפוח: 'care',
  ברבר: 'barber',
  שיניים: 'dental',
  וטרינר: 'vet',
};

/** Niqqud, cantillation marks, geresh and gershayim. */
const HEBREW_MARKS = /[֑-ׇ׳״]/g;

/** Unicode combining marks left behind by NFD on accented Latin letters. */
const COMBINING_MARKS = /[̀-ͯ]/g;

/**
 * Cut `slug` to at most `max` characters, preferring a hyphen boundary so a
 * word is never sliced in half. A single word longer than `max` is hard-cut,
 * since there is no boundary to prefer.
 */
function truncateOnWordBoundary(slug: string, max: number): string {
  if (slug.length <= max) return slug;
  const cut = slug.slice(0, max);
  const lastHyphen = cut.lastIndexOf('-');
  const trimmed = lastHyphen > 0 ? cut.slice(0, lastHyphen) : cut;
  return trimmed.replace(/-+$/, '');
}

/**
 * Sanitize what the owner types directly into the slug field, live, on every
 * keystroke: lowercase, any run of whitespace becomes a single dash, and
 * anything left that isn't `[a-z0-9-]` is dropped outright (so it never
 * silently reappears once the owner backspaces past it). Repeated dashes
 * collapse to one and a leading dash is stripped (typing a leading space
 * would otherwise "eat" the first keystroke).
 *
 * Deliberately does NOT strip a trailing dash — the owner is mid-word right
 * after pressing space, and trimming it there would make the space a no-op.
 * `finalizeSlugInput` does that trim once, at submit time.
 */
export function sanitizeSlugInput(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+/, '');
}

/**
 * Final cleanup for a hand-typed slug right before it's sent to the server —
 * strips the trailing dash `sanitizeSlugInput` deliberately leaves in place
 * while typing. The api's `createOwn` slug regex
 * (`/^[a-z0-9]+(?:-[a-z0-9]+)*$/`) rejects a trailing dash outright.
 */
export function finalizeSlugInput(raw: string): string {
  return raw.replace(/-+$/, '');
}

/**
 * Turn a business name into a lowercase kebab-case ASCII slug.
 * Always returns a string matching /^[a-z0-9]+(?:-[a-z0-9]+)*$/.
 */
export function generateSlug(name: string): string {
  const bare = name.replace(HEBREW_MARKS, '');

  const transliterated = bare
    .split(/\s+/)
    .map((word) => {
      const dictHit = HEBREW_TRADE_WORDS[word];
      if (dictHit !== undefined) return dictHit;
      return Array.from(word)
        .map((ch) => HEBREW_TRANSLITERATION[ch] ?? ch)
        .join('');
    })
    .join(' ');

  const ascii = transliterated.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase();

  const kebab = ascii.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  if (kebab === '') return FALLBACK_SLUG;
  return truncateOnWordBoundary(kebab, SLUG_MAX_LENGTH);
}

function defaultRandomInt(maxExclusive: number): number {
  return Math.floor(Math.random() * maxExclusive);
}

function randomSuffix(randomInt: (maxExclusive: number) => number): string {
  let out = '';
  for (let i = 0; i < SLUG_SUFFIX_LENGTH; i++) {
    out += SLUG_SUFFIX_ALPHABET[randomInt(SLUG_SUFFIX_ALPHABET.length)];
  }
  return out;
}

/**
 * A content-free slug suggestion for a brand-new business: `business-<4
 * random chars>`, e.g. `business-8f3k`. Used as the slug field's starting
 * value instead of an auto-translation of the business name — Hebrew has no
 * reliable way to guess the vowels a human would write (see
 * `HEBREW_TRANSLITERATION` above), so a translated guess reads as noise more
 * often than it reads as a real word. A random code is never embarrassing,
 * just unpersonalized; the field stays fully editable and the owner is
 * expected to replace it with something meaningful.
 *
 * `randomInt` is injectable so tests are deterministic.
 */
export function randomSlugSuggestion(
  randomInt: (maxExclusive: number) => number = defaultRandomInt,
): string {
  return `${FALLBACK_SLUG}-${randomSuffix(randomInt)}`;
}

/** How many suffixed candidates to try before giving up. */
const MAX_SUFFIX_ATTEMPTS = 5;

/**
 * Resolve `base` to a slug that `exists` reports as free, appending a random
 * suffix on collision (`salon-noya` -> `salon-noya-4x9k`).
 *
 * A RANDOM suffix rather than a sequential `-2`/`-3`: sequential needs a
 * read-modify-retry loop that races under concurrency, whereas this collides
 * only by chance and needs no ordering. The DB's unique index remains the real
 * authority — a caller must still handle a P2002 at insert time.
 *
 * `randomInt` is injectable so tests are deterministic.
 */
export async function ensureUniqueSlug(
  base: string,
  exists: (slug: string) => Promise<boolean>,
  randomInt: (maxExclusive: number) => number = defaultRandomInt,
): Promise<string> {
  if (!(await exists(base))) return base;

  const trimmed = truncateOnWordBoundary(base, SLUG_MAX_LENGTH - SLUG_SUFFIX_LENGTH - 1);

  for (let attempt = 0; attempt < MAX_SUFFIX_ATTEMPTS; attempt++) {
    const candidate = `${trimmed}-${randomSuffix(randomInt)}`;
    if (!(await exists(candidate))) return candidate;
  }

  throw new Error(`Could not find a free slug for "${base}" after ${MAX_SUFFIX_ATTEMPTS} attempts`);
}
